import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { TaMaps } from '@components/shared/Maps/TaMaps';
import { useGeolocated } from '@components/shared/Maps/useGeoLocated';
import { useCurrentAddress } from '@components/shared/Maps/utils';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { AutocompleteField } from '@features/FindSpecialist/components/AutocompleteField';
import { CropAreaAndUnit } from '@features/FindSpecialist/components/CropAndUnit';
import { LocationInput } from '@features/FindSpecialist/components/LocationInput';
import {
	// useGetCurrentStepData,
	useGetFilteredDrafts,
} from '@features/FindSpecialist/store/findSpecialistHooks';
import {
	ExpressRequestInitialStep,
	SeasonalRequestInitialStep,
	submitInitialStep,
} from '@features/FindSpecialist/store/findSpecialistService';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { useModalDimensions } from '@hooks/useModalDimensions';
import { mdiChevronRight } from '@mdi/js';
import Icon from '@mdi/react';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import { Backdrop, Button, CircularProgress } from '@mui/material';
import { mapToDictionary } from '@utils/util.functions';
import { Nullable } from '@utils/util.types';
import { Dayjs } from 'dayjs';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { useCrops, useCropsByIdsPost, useCropsStore } from 'services/crops';
import { useLanguages, useLanguagesByIds, useLanguagesStore } from 'services/languages';
import { useServices, useServicesStore } from 'services/services';
import {
	useGetUserByConnectionCode,
	useGetUserByCropsAndLanguages,
} from 'services/users/usersHook';
import { useExpressStore } from '../../store/index';
import { ConsultationTypeSelector } from './ConsultationTypeSelector';
import { SeasonalConsultationDates } from './SeasonalConsultationDates';
import { ServicesAutocomplete } from './ServicesAutocomplete';
import { SpecialistCodeInput } from './SpecialistCodeInput';
import {
	cropUnitOptions,
	ExpressFormValues,
	expressInitialValues,
	expressValidationSchema,
	findSpecialistInitialValues,
	findSpecialistValidationSchema,
	SearchFormValues,
} from './utils';
import { Dictionary } from '@utils/util.types';
import { mutate } from 'swr';
import * as Yup from 'yup';

const backdropStyle = {
	color: '#fff',
	zIndex: (theme: any) => theme.zIndex.drawer + 1 + 1000,
	display: 'flex',
	flexDirection: 'column',
	gap: 2,
};

// Define onSubmit function type
type OnSubmitFunction = (values: ExpressFormValues | SearchFormValues) => Promise<void>;

// Define handleResetForm function type
type HandleResetFormFunction = () => void;

export const SearchStep = () => {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { crops, filteredCrops, setFilteredCrops } = useCropsStore();
	const { languages, setLanguages } = useLanguagesStore();
	const [selectedServiceObjects, setSelectedServiceObjects] = useState<any[]>([]);
	const [mapOpen, setMapOpen] = useState(false);
	const [specialistIds, setSpecialistIds] = useState<string[]>([]);
	const [isUserLoading, setIsUserLoading] = useState(false);
	const [resetServicesFlag, setResetServicesFlag] = useState(false);

	const formDataInitialized = useRef(false);

	const { pathname } = useLocation();
	const { requestId: urlRequestId } = useParams();
	const { coords } = useGeolocated();
	const { address, loading: addressLoading } = useCurrentAddress();
	const modalDimensions = useModalDimensions();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

	const { services } = useServicesStore();
	const { expressData, filteredDrafts } = useExpressStore();
	const { fetchCropsByIds } = useCropsByIdsPost();
	const { fetchLanguagesByIds } = useLanguagesByIds();
	const { mutate: mutateCrops } = useCrops();
	const { mutate: mutateLanguages } = useLanguages();
	const { getUserByCode } = useGetUserByConnectionCode();
	
	// Define cropUnitOptions here for consistent access throughout the component
	const cropUnitOptions = useMemo(() => [
		{ name: t('unitTypes.m2'), id: 'm2' },
		{ name: t('unitTypes.hectares'), id: 'hectares' },
		{ name: t('unitTypes.acres'), id: 'acres' },
	], [t]);

	// Derived state
	const isFindSpecialistForm = useMemo(() => !pathname.includes('express'), [pathname]);

	const position = useMemo(
		() => ({
			lat: coords?.latitude || 0,
			lng: coords?.longitude || 0,
		}),
		[coords]
	);

	const cropsDictionary = useMemo(() => mapToDictionary(crops), [crops]);
	const filteredCropsDictionary = useMemo(() => mapToDictionary(filteredCrops), [filteredCrops]);
	const languagesDictionary = useMemo(() => mapToDictionary(languages), [languages]);

	const initialFormValues = useMemo(
		() => (!isFindSpecialistForm ? expressInitialValues : findSpecialistInitialValues),
		[isFindSpecialistForm]
	);

	const validationSchema = useMemo(
		() => (!isFindSpecialistForm ? expressValidationSchema : findSpecialistValidationSchema),
		[isFindSpecialistForm]
	);

	const routeBasedOnConsultationType = useMemo(() => {
		return !isFindSpecialistForm
			? '/farmer/services/express/description'
			: '/farmer/services/specialist-request/description';
	}, [isFindSpecialistForm]);

	const currentRouteBasedOnConsultationType = useMemo(() => {
		return !isFindSpecialistForm
			? '/farmer/services/express/search'
			: '/farmer/services/specialist-request/search';
	}, [isFindSpecialistForm]);

	// Forward declaration of functions to avoid circular references
	const handleResetFormRef = useRef<HandleResetFormFunction | null>(null);
	const onSubmitRef = useRef<OnSubmitFunction | null>(null);

	// Initialize form
	const formik = useFormik<ExpressFormValues | SearchFormValues>({
		initialValues: initialFormValues,
		validationSchema: validationSchema,
		onSubmit: async (values) => {
			if (onSubmitRef.current) {
				await onSubmitRef.current(values);
			}
		},
	});

	const { values, setFieldValue, resetForm, setValues, handleSubmit, touched, errors, isValid } =
		formik;

	// Handlers
	const handleResetForm: HandleResetFormFunction = useCallback(() => {
		resetForm();
		setSelectedServiceObjects([]);
		// Reset specialist IDs to empty array
		setSpecialistIds([]);
		console.log('Reset form and cleared specialist IDs');
	}, [resetForm]);

	// Set the ref after defining the function
	handleResetFormRef.current = handleResetForm;

	// Define onSubmit function
	const onSubmit: OnSubmitFunction = useCallback(async (values: ExpressFormValues | SearchFormValues) => {
		try {
			console.log('Form submitted with values:', values);
			console.log('Current specialist IDs:', specialistIds);
			console.log('Crop unit being sent:', values.crop_unit);
			
			// Log date values for debugging
			if ((values as SearchFormValues).consultationType === 'SEASONAL') {
				const startDate = (values as SearchFormValues).startDate;
				const endDate = (values as SearchFormValues).endDate;
				
				console.log('Start date object:', startDate);
				console.log('End date object:', endDate);
				
				if (startDate && typeof startDate.format === 'function') {
					console.log('Formatted start date:', startDate.format('YYYY-MM-DD HH:mm:ss'));
				}
				
				if (endDate && typeof endDate.format === 'function') {
					console.log('Formatted end date:', endDate.format('YYYY-MM-DD HH:mm:ss'));
				}
			}

			const formattedValuesForExpress: ExpressRequestInitialStep = {
				specialist_id: specialistIds,
				services_id: values.service_id as string[],
				request_type: (values as SearchFormValues).consultationType || 'EXPRESS',
				crop_id: values.crops?.id || '',
				area: Number(values.crop_area),
				unit: values.crop_unit.name, // Use name as before for API compatibility
				location_name: values.crop_location_name,
				latitude: Number(values.crop_latitude),
				longitude: Number(values.crop_longitude),
				communication_language: values.userLanguages.map((language: Dictionary) => language.id),
				request_id: urlRequestId || null,
				connection_code: values.code || undefined, // Add the connection_code field
			};

			console.log('Submitting request with connection_code:', values.code);
			console.log('Specialist IDs being sent to API:', specialistIds);
			console.log('Formatted values for API:', formattedValuesForExpress);

			// Helper function to safely format dates
			const formatDayjsDate = (date: Nullable<Dayjs>): string | undefined => {
				if (!date) return undefined;
				if (typeof date.format !== 'function') {
					console.warn('Date is not a proper Dayjs object:', date);
					return undefined;
				}
				return date.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
			};

			const formattedValuesForSeasonal: SeasonalRequestInitialStep = {
				...formattedValuesForExpress,
				start_date: formatDayjsDate((values as SearchFormValues).startDate),
				end_date: formatDayjsDate((values as SearchFormValues).endDate),
			};

			const response = await submitInitialStep(
				!isFindSpecialistForm ? formattedValuesForExpress : formattedValuesForSeasonal
			);

			console.log('API response:', response);

			if (response.success) {
				setResetServicesFlag(true);
				setTimeout(() => {
					handleResetForm();
					setResetServicesFlag(false);
				}, 100);

				navigateWithLanguage(`${routeBasedOnConsultationType}/${response.data.request_id}`);
			} else {
				showSnackbar(t('error'), t('errorSubmittingInitialStep'), 'error');
			}
		} catch (error) {
			console.error('Error submitting form:', error);
			showSnackbar(t('error'), t('errorSubmittingInitialStep'), 'error');
		}
	}, [
		specialistIds,
		urlRequestId,
		isFindSpecialistForm,
		submitInitialStep,
		setResetServicesFlag,
		handleResetForm,
		navigateWithLanguage,
		routeBasedOnConsultationType,
		showSnackbar,
		t
	]);

	// Set the ref after defining the function
	onSubmitRef.current = onSubmit;

	const handleLocationSelect = useCallback(
		(address: string, lat: number, lng: number) => {
			setFieldValue('crop_location_name', address);
			setFieldValue('crop_latitude', lat);
			setFieldValue('crop_longitude', lng);
		},
		[setFieldValue]
	);

	const handleDateChange = useCallback(
		(type: 'start' | 'end') => (newValue: Nullable<Dayjs>) => {
			// If newValue is null, set the field to null
			if (!newValue) {
				setFieldValue(type === 'start' ? 'startDate' : 'endDate', null);
				return;
			}
			
			// Ensure we're working with a valid Dayjs object
			if (typeof newValue.hour !== 'function') {
				console.warn('Invalid date object received:', newValue);
				return;
			}
			
			try {
				if (type === 'start') {
					// Set time to start of day for consistent behavior
					const adjustedDate = newValue.hour(0).minute(0).second(0).millisecond(0);
					console.log('Setting start date to:', adjustedDate.format('YYYY-MM-DD HH:mm:ss'));
					setFieldValue('startDate', adjustedDate);
				} else {
					// Don't allow end date before start date
					if (
						(values as SearchFormValues).startDate &&
						newValue.isBefore((values as SearchFormValues).startDate)
					) {
						console.warn('End date cannot be before start date');
						return;
					}

					// Set time to end of day for end date to include the full day
					const adjustedDate = newValue.hour(23).minute(59).second(59).millisecond(999);
					console.log('Setting end date to:', adjustedDate.format('YYYY-MM-DD HH:mm:ss'));
					setFieldValue('endDate', adjustedDate);
				}
			} catch (error) {
				console.error('Error adjusting date:', error);
			}
		},
		[setFieldValue, values]
	);

	const handleDebouncedCode = useCallback(
		async (value: string) => {
			if (value.length >= 6) {
				setIsUserLoading(true);
				try {
					console.log('Fetching specialist with code:', value);
					const user: any = await getUserByCode(value);
					console.log('Found specialist by code:', user);
					if (user?.user_crops) {
						const cropsByIds = await fetchCropsByIds(
							user.user_crops.map((crop: any) => crop.crop_id)
						);

						setFilteredCrops(cropsByIds);

						if (user?.user_languages) {
							const languagesByIds = await fetchLanguagesByIds(
								user.user_languages.map((language: any) => language.language_id)
							);

							setLanguages(languagesByIds);
						}
					}

					// Replace specialist_id instead of appending
					setSpecialistIds([user?.user_id].filter(Boolean));
					console.log('Set specialist IDs to:', [user?.user_id].filter(Boolean));
					
					setFieldValue('crops', null);
					setFieldValue('userLanguages', []);
					setIsUserLoading(false);
				} catch (error) {
					console.error('Error fetching specialist by code:', error);
					showSnackbar(t('error'), t('specialistNotFound'), 'error');
					setIsUserLoading(false);
				}
			} else if (value.length === 0) {
				// Clear specialist IDs when code is empty
				setSpecialistIds([]);
				console.log('Cleared specialist IDs');
				
				mutateCrops();
				mutateLanguages();
				setIsUserLoading(false);
			}
		},
		[
			getUserByCode,
			setFieldValue,
			fetchCropsByIds,
			setFilteredCrops,
			fetchLanguagesByIds,
			setLanguages,
			showSnackbar,
			t,
			mutateCrops,
			mutateLanguages,
			values
		]
	);

	const handleConsultationTypeChange = useCallback(
		(type: string) => {
			setFieldValue('consultationType', type);

			resetForm({
				values: {
					...findSpecialistInitialValues,
					consultationType: type,
				} as any,
			});

			navigateWithLanguage(currentRouteBasedOnConsultationType);
		},
		[setFieldValue, resetForm, navigateWithLanguage, currentRouteBasedOnConsultationType]
	);

	const handleServicesChange = useCallback(
		(services: any[]) => {
			// For single select, we'll always get an array with either 0 or 1 items
			setSelectedServiceObjects(services);
			setFieldValue(
				'service_id',
				services.length > 0 ? [services[0].id] : []
			);
		},
		[setFieldValue]
	);

	// Side effects
	useEffect(() => {
		if (!urlRequestId) {
			handleResetForm();
		}
	}, [urlRequestId, handleResetForm]);

	// Set up filtered drafts
	const consultationType =
		((values as SearchFormValues).consultationType?.toLowerCase() as any) || 'express';
	useGetFilteredDrafts(consultationType);
	useServices();

	const memoizedCropId = useMemo(
		() => (values.code ? undefined : values.crops?.id),
		[values.code, values.crops]
	);

	const { specialists, isLoading: specialistsLoading } = useGetUserByCropsAndLanguages(
		memoizedCropId,
		values.userLanguages?.map((language: Dictionary) => language.id)
	);

	// Update position details from geolocation
	useEffect(() => {
		setFieldValue('crop_latitude', position?.lat || 0);
		setFieldValue('crop_longitude', position?.lng || 0);
		setFieldValue('crop_location_name', address);
	}, [address, position?.lat, position?.lng, setFieldValue]);

	// Set specialist IDs when specialists data is loaded
	useEffect(() => {
		if (specialists?.data) {
			setSpecialistIds(specialists.data.map((specialist) => specialist.user_id || ''));
		}
	}, [specialists]);

	// Initialize form with existing data if available
	useEffect(() => {
		if (urlRequestId && expressData && !formDataInitialized.current) {
			console.log('Initializing form with expressData:', expressData);
			console.log('Connection code from API:', expressData.connection_code);
			console.log('Unit from API:', expressData.unit);
			
			// Find the correct crop unit option by id or name
			let unitOption = cropUnitOptions.find((unit) => unit.id === expressData.unit);
			
			// If not found by id, try to find by name
			if (!unitOption) {
				unitOption = cropUnitOptions.find((unit) => unit.name === expressData.unit);
			}
			
			// If still not found, use a default
			if (!unitOption) {
				unitOption = cropUnitOptions.find(unit => unit.id === 'hectares') || cropUnitOptions[0];
				console.log('No matching unit found, using default:', unitOption);
			} else {
				console.log('Found unit option:', unitOption);
			}
			
			const stepData = {
				service_id: expressData.services_id || [],
				specialist_id: expressData.specialist_id || [], // Use specialist_id from API response instead of empty array
				crops: expressData.crop_id
					? cropsDictionary.find((crop) => crop.id === expressData.crop_id) || null
					: null,
				crop_area: expressData.area || 0,
				crop_unit: unitOption,
				userLanguages: expressData.communication_language
					? languagesDictionary.filter((language) =>
							expressData.communication_language.includes(language.id)
						)
					: [],
				startDate: expressData.start_date || undefined,
				endDate: expressData.end_date || undefined,
				consultationType: expressData.request_type || 'EXPRESS',
				crop_location_name: expressData.location_name || '',
				crop_latitude: expressData.latitude || 0,
				crop_longitude: expressData.longitude || 0,
				code: expressData.connection_code || '', // Use connection_code from API response
			};

			console.log('Setting form values with stepData:', stepData);
			setValues((currentValues: ExpressFormValues | SearchFormValues) => ({ ...currentValues, ...stepData }) as any);
			
			// Set specialist IDs from API response instead of resetting to empty array
			if (expressData.specialist_id && Array.isArray(expressData.specialist_id) && expressData.specialist_id.length > 0) {
				console.log('Setting specialist IDs from API response:', expressData.specialist_id);
				setSpecialistIds(expressData.specialist_id);
			} else if (expressData.connection_code) {
				// If we have a connection code but no specialist IDs, we'll fetch them via the code
				console.log('No specialist IDs in API response, will try to fetch via connection code');
			} else {
				console.log('No specialist IDs or connection code in API response, setting empty array');
				setSpecialistIds([]);
			}

			if (expressData.services_id && services) {
				const transformedServices = services.flatMap((serviceGroup) =>
					serviceGroup.children.map((service: any) => ({
						id: service.id,
						name: service.name,
						category: serviceGroup.name,
						price: service.price,
						unit: service.unit,
					}))
				);

				const serviceObjects = expressData.services_id
					.map((serviceId: string) =>
						transformedServices.find((service) => service.id === serviceId)
					)
					.filter(Boolean);

				setSelectedServiceObjects(serviceObjects);
			}

			// If we have a connection code, process it to fetch specialist data
			if (expressData.connection_code && expressData.connection_code.length >= 6) {
				console.log('Processing connection code from API:', expressData.connection_code);
				handleDebouncedCode(expressData.connection_code);
			}

			formDataInitialized.current = true;
		}
	}, [expressData, cropsDictionary, languagesDictionary, urlRequestId, setValues, services, handleDebouncedCode, cropUnitOptions]);

	// Reset the form initialization flag when URL request ID changes
	useEffect(() => {
		formDataInitialized.current = false;
	}, [urlRequestId]);

	// Ensure crop_unit is properly initialized
	useEffect(() => {
		// Only run once on component mount
		const defaultUnit = cropUnitOptions.find(option => option.id === 'hectares') || cropUnitOptions[0];
		
		if (!values.crop_unit || 
			(typeof values.crop_unit === 'string' && values.crop_unit === 'undefined') || 
			(typeof values.crop_unit === 'object' && values.crop_unit?.name === 'undefined')) {
			console.log('Initializing crop_unit with default:', defaultUnit);
			setFieldValue('crop_unit', defaultUnit);
		}
	}, [cropUnitOptions, setFieldValue, values.crop_unit]);

	// Loading state
	const isLoading = specialistsLoading || isUserLoading;

	// Log the code field whenever it changes
	useEffect(() => {
		console.log('Current code value:', values.code);
	}, [values.code]);

	// Log the unit field whenever it changes
	useEffect(() => {
		console.log('Current crop_unit value:', values.crop_unit);
	}, [values.crop_unit]);

	// Log expressData when it changes
	useEffect(() => {
		if (expressData) {
			console.log('expressData updated:', expressData);
			console.log('specialist_id in expressData:', expressData.specialist_id);
		}
	}, [expressData]);

	// Force revalidation of getCurrentStepData SWR cache when component mounts with a requestId
	useEffect(() => {
		if (urlRequestId) {
			console.log('Forcing revalidation of getCurrentStepData cache for requestId:', urlRequestId);
			mutate(['getCurrentStepData', urlRequestId]);
		}
	}, [urlRequestId]);

	return (
		<form
			onSubmit={handleSubmit}
			className="flex flex-col xs:mt-0 h-full gap-[20px] p-[20px] relative justify-between"
		>
			<div className="flex items-center h-full justify-center sm:mt-0 mt-[20px]">
				<div className="flex flex-wrap flex-col max-w-[500px] w-full gap-[20px]">
					<h1 className="text-dark text-center font-bold text-xl leading-normal mb-[20px] mt-[50px]">
						<SearchOutlinedIcon /> {t('findSpecialistPage.findYourSpecialist')}
					</h1>

					{!isFindSpecialistForm && (
						<ServicesAutocomplete
							userServices={expressData?.services_id}
							services={services}
							resetServices={resetServicesFlag}
							selectedServices={selectedServiceObjects}
							onSelect={handleServicesChange}
						/>
					)}

					{isFindSpecialistForm && (
						<ConsultationTypeSelector
							selectedType={(values as SearchFormValues).consultationType}
							onSelect={handleConsultationTypeChange}
						/>
					)}

					<SpecialistCodeInput formik={formik} handleChange={handleDebouncedCode} />

					{(values as SearchFormValues).consultationType === 'SEASONAL' && (
						<SeasonalConsultationDates
							startDate={(values as SearchFormValues).startDate}
							endDate={(values as SearchFormValues).endDate}
							onStartChange={handleDateChange('start')}
							onEndChange={handleDateChange('end')}
						/>
					)}

					<AutocompleteField
						formik={formik}
						name="crops"
						label={t('selectCrop')}
						options={filteredCropsDictionary.length > 0 ? filteredCropsDictionary : cropsDictionary}
						icon={<SpaOutlinedIcon color={touched.crops && errors.crops ? 'error' : 'primary'} />}
					/>

					<CropAreaAndUnit formik={formik} cropUnitOptions={cropUnitOptions} />

					<LocationInput
						formik={formik}
						loading={addressLoading}
						openMapsModal={() => setMapOpen(true)}
					/>

					<AutocompleteField
						formik={formik}
						multiple
						name="userLanguages"
						label={t('communicationLanguage')}
						options={languagesDictionary}
						icon={
							<RecordVoiceOverOutlinedIcon
								color={touched.userLanguages && errors.userLanguages ? 'error' : 'primary'}
							/>
						}
					/>

					<Button
						disabled={!isValid}
						className="w-full"
						type="submit"
						variant="contained"
						endIcon={<Icon path={mdiChevronRight} size={1} />}
					>
						{t('next')}
					</Button>

					<Button
						className="w-full"
						onClick={() => {
							navigateWithLanguage(currentRouteBasedOnConsultationType);
							handleResetForm();
						}}
						variant="outlined"
						endIcon={<Icon path={mdiChevronRight} size={1} />}
					>
						{t('resetForm')}
					</Button>
				</div>
			</div>

			{/* Loading overlay */}
			<Backdrop sx={backdropStyle} open={isLoading}>
				<CircularProgress color="primary" />
			</Backdrop>

			{/* Map modal */}
			<GeneralMuiModal
				maxWidth="xl"
				title={t('myFarm.farmLocation')}
				styles={{
					paperStyle: {
						width: modalDimensions.width,
						height: modalDimensions.height,
						padding: 0,
					},
					contentStyle: { padding: 0 },
				}}
				open={mapOpen}
				onClose={() => setMapOpen(false)}
				content={
					<TaMaps
						onSave={() => setMapOpen(false)}
						currentAddress={values.crop_location_name}
						marker={{
							lat: Number(values.crop_latitude),
							lng: Number(values.crop_longitude),
						}}
						onLocationSelect={handleLocationSelect}
					/>
				}
			/>

			{/* Drafts section - currently commented out in original code */}
			{/* <div className="mt-[40px]">
				<h6 className="text-dark text-center font-roboto text-xl font-bold leading-[140%]">
					{t('drafts')}
				</h6>
				<DraftsTable
					consultationType={
						((values as SearchFormValues).consultationType?.toLowerCase() as RequestType) ||
						'express'
					}
					draftExpressData={filteredDrafts}
					handleRequestClick={handleRequestClick}
				/>
			</div> */}
		</form>
	);
};
